import { Grid } from '@mui/material'
import { ChipLabel, getHydroCote } from 'components/echart/ChartOptionsChip'
import { yAutomaticScaleValues } from 'components/echart/EChartUtils'
import EChartV6 from 'components/echart/EChartV6'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import { HYDRO_MEASURE_COTE, HYDROMETER_HEIGHT_TYPE } from 'hydrometry/constants/HydrometryConstants'
import { compact, groupBy, isUndefined, keys, max, orderBy, round, startsWith, uniq } from 'lodash'
import { DIAGRAPHIE_PERTUIS } from 'quality/constants/QualityConstants'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PiezometerStationAction from 'station/actions/PiezometerStationAction'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { blackColor, greyBlue, mainBlue } from 'utils/constants/ColorTheme'
import { getNGFDepth } from 'utils/HydroUtils'

const DiagraphieChart = ({}) => {
    const [fillwayThresholds, setFillwayThresholds] = useState([])
    const [hydrometricStation, setHydrometricStation] = useState()
    const [displayCote, setDisplayCote] = useState(HYDRO_MEASURE_COTE.HEIGHT)

    const {
        operationAnalysis,
        operation,
        parametersIndex,
        unitsIndex,
        hydrometryThresholds,
        piezometerThresholds,
        hydroLastMeasures,
        associatedSites,
    } = useSelector(store => ({
        operationAnalysis: store.OperationReducer.operationAnalysis,
        operation: store.OperationReducer.operation,
        parametersIndex: store.ParameterReducer.parametersIndex,
        unitsIndex: store.UnitReducer.unitsIndex,
        hydrometryThresholds: store.HydrometryReducer.hydrometryThresholds,
        piezometerThresholds: store.PiezometerStationReducer.piezometerThresholds,
        hydroLastMeasures: store.HydrometryReducer.hydroLastMeasures,
        associatedSites: store.StationReducer.associatedSites,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [hydroSite, piezoSite] = useMemo(() => ([
        associatedSites.find(({ stationLinkedType }) => stationLinkedType === STATION_TYPE_CONSTANT.HYDROMETRY),
        associatedSites.find(({ stationLinkedType }) => stationLinkedType === STATION_TYPE_CONSTANT.PIEZOMETRY),
    ]), [associatedSites])

    useEffect(() => {
        if (hydroSite) {
            dispatch(HydrometryAction.fetchHydrometricThresholds())
            dispatch(HydrometryAction.fetchHydroLastMeasures([hydroSite.stationLinkedId], HYDROMETER_HEIGHT_TYPE, false, undefined, undefined, operation.date))
            Promise.all([HydrometryAction.promiseHydrometricStation(hydroSite.stationLinkedId)]).then((station = []) => {
                setHydrometricStation(station?.[0])
            })
        } else if (piezoSite) {
            dispatch(PiezometerStationAction.fetchPiezometerThresholds())
        }
    }, [hydroSite, piezoSite])

    useEffect(() => {
        if (hydroSite && hydrometryThresholds?.length) {
            const hydroSiteThresholds = hydrometryThresholds.filter(({ stationId, title }) => parseInt(stationId) === hydroSite.stationLinkedId && startsWith(title, DIAGRAPHIE_PERTUIS))
            setFillwayThresholds(hydroSiteThresholds)
        } else if (piezoSite && piezometerThresholds?.length) {
            const piezoSiteThresholds = piezometerThresholds.filter(({ stationId, title }) => parseInt(stationId) === piezoSite.stationLinkedId && startsWith(title, DIAGRAPHIE_PERTUIS))
            setFillwayThresholds(piezoSiteThresholds)
        }
    }, [hydrometryThresholds, piezometerThresholds])

    const NGFDepth = useMemo(() => {
        if (displayCote === HYDRO_MEASURE_COTE.HEIGHT) return 0
        return getNGFDepth(hydrometricStation)
    }, [displayCote, hydrometricStation])

    const groupedAnalysisByParameter = useMemo(() => groupBy(operationAnalysis.map(ana => ({ ...ana, depth: ana.depth + NGFDepth })), 'parameter'), [NGFDepth, operationAnalysis])

    const allDepths = useMemo(() => orderBy(uniq(operationAnalysis.map(a => a.depth + NGFDepth))), [NGFDepth, operationAnalysis])
    const paramHeaders = keys(groupedAnalysisByParameter).map(parameter => {
        const parameterInfo = parametersIndex[parameter]
        const unit = unitsIndex[groupedAnalysisByParameter[parameter][0].unit]
        return `${parameterInfo.name} (${unit.symbol})`
    })
    const exportDatas = useMemo(() => allDepths.map(depth => {
        const row = { [i18n.depthWithUnit]: depth }
        keys(groupedAnalysisByParameter).forEach((parameter, idx) => {
            const analysis = groupedAnalysisByParameter[parameter].find(a => a.depth === depth)
            row[paramHeaders[idx]] = analysis?.result
        })
        return row
    }), [NGFDepth, groupedAnalysisByParameter, allDepths])
    const exportHeaders = [i18n.depthWithUnit, ...paramHeaders]

    const nbParams = keys(groupedAnalysisByParameter).length
    const leftSpacing = 10
    const leftNameGap = 50
    const heightGridWidth = 100
    const minGridPx = 140
    const gridHeight = 600
    const minTotalPx = leftSpacing + leftNameGap + heightGridWidth + (nbParams * minGridPx)

    const grid = [
        {
            id: i18n.height,
            left: leftSpacing + leftNameGap,
            width: heightGridWidth,
            top: 100,
            height: gridHeight,
        },
        ...keys(groupedAnalysisByParameter).map((parameter, index) => ({
            id: parameter,
            left: leftSpacing + leftNameGap + heightGridWidth + index * minGridPx,
            width: minGridPx,
            top: 100,
            height: gridHeight,
        })),
    ]

    const xAxis = [
        {
            gridIndex: 0,
            position: 'top',
            name: i18n.height,
            nameGap: 25,
            nameLocation: 'center',
            nameTextStyle: {
                fontWeight: 'bold',
            },
            min: 0,
            max: 1,
            interval: 1,
            splitLine: {
                lineStyle: {
                    color: blackColor,
                },
            },
            axisLabel: { show: false },
        },
        ...keys(groupedAnalysisByParameter).map((parameter, index) => {
            const analyses = groupedAnalysisByParameter[parameter]
            const values = compact(analyses.map(a => a.result))
            const { min, max: maxValue } = yAutomaticScaleValues(values)
            const parameterInfo = parametersIndex[parameter]
            const unit = unitsIndex[analyses[0].unit]
            return ({
                gridIndex: index + 1,
                type: 'value',
                position: 'top',
                name: `${parameterInfo.displayName} (${unit.symbol})`,
                nameGap: 25,
                nameLocation: 'center',
                nameTextStyle: {
                    fontWeight: 'bold',
                },
                nameTruncate: {
                    maxWidth: 135,
                },
                min: min || 0,
                max: maxValue || 1,
                interval: maxValue - min || 1,
                axisLabel: {
                    formatter: v => round(v, 2),
                    alignMaxLabel: 'right',
                    alignMinLabel: 'left',
                    padding: 4,
                    margin: 0,
                },
                axisTick: {
                    length: 50,
                },
                tooltip: {
                    show: true,
                    formatter: `${parameterInfo.name} (${unit.symbol})`,
                },
            })
        }),
    ]

    const depthValues = useMemo(() => compact(operationAnalysis.map(a => a.depth + NGFDepth)), [NGFDepth, operationAnalysis])
    const yAxisBase = useMemo(() => ({
        type: 'value',
        inverse: true,
        min: 0,
        max: round(max(depthValues), -1) + 10,
        axisPointer: { snap: true },
    }), [depthValues])

    const yAxis = [
        {
            ...yAxisBase,
            gridIndex: 0,
            name: i18n.depthWithUnit,
            nameLocation: 'center',
            nameGap: leftNameGap,
            position: 'left',
            axisPointer: { ...yAxisBase.axisPointer, label: { show: true } },

        },
        ...keys(groupedAnalysisByParameter).map((_, index) => ({
            ...yAxisBase,
            gridIndex: index + 1,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
        })),
    ]

    const hydroLastMeasure = useMemo(() => hydroLastMeasures?.length ? hydroLastMeasures[0].value + NGFDepth : undefined, [NGFDepth, hydroLastMeasures])

    const series = [
        {
            name: i18n.height,
            type: 'line',
            symbol: 'none',
            lineStyle: { opacity: 0 },
            data: compact([
                hydroLastMeasure,
            ]),
            markArea: {
                silent: true,
                label: {
                    position: 'insideTop',
                },
                itemStyle: {
                    color: mainBlue,
                    opacity: 0.9,
                },
                data: compact([
                    hydroLastMeasures.length ? [
                        {
                            name: `${round(hydroLastMeasure, 3)} ${i18n.metersSymbol}`,
                            yAxis: round(hydroLastMeasure, 3),
                        },
                        {
                            x: leftNameGap + leftSpacing + 100,
                            y: gridHeight + 100,
                        },
                    ] : undefined,
                ]),
            },
        },
        {
            name: i18n.strait,
            type: 'line',
            symbol: 'none',
            lineStyle: { opacity: 0 },
            data: compact([
                ...fillwayThresholds.flatMap(f => [
                    [0, f.month01 + NGFDepth],
                    [0, f.month02 + NGFDepth],
                ]),
            ]),
            markArea: {
                silent: false,
                label: {
                    show: true,
                    position: 'insideLeft',
                    formatter: '{b}',
                },
                emphasis: {
                    itemStyle: {
                        color: greyBlue,
                        opacity: 1,
                    },
                    label: {
                        show: true,
                        position: 'insideLeft',
                        formatter: ({ data }) => `${data.yAxis}${i18n.metersSymbol} → ${data.endFillway}${i18n.metersSymbol}`,
                    },
                },
                itemStyle: {
                    color: greyBlue,
                    opacity: 1,
                },
                data: fillwayThresholds.map(fillway => ([
                    {
                        name: fillway.title,
                        yAxis: fillway.month01 + NGFDepth,
                        endFillway: fillway.month02 + NGFDepth,
                    },
                    {
                        yAxis: fillway.month02 + NGFDepth,
                    },
                ])),
            },
        },
        ...keys(groupedAnalysisByParameter).map((parameter, index) => {
            const analyses = groupedAnalysisByParameter[parameter]
            const parameterInfo = parametersIndex[parameter]
            const unit = unitsIndex[analyses[0].unit]
            return ({
                xAxisIndex: index + 1,
                yAxisIndex: index + 1,
                name: `${parameterInfo.shortLabel} (${unit.symbol})`,
                type: 'line',
                symbol: 'none',
                smooth: true,
                data: orderBy(compact(analyses.map(a => !isUndefined(a.result) ? [a.result, a.depth] : undefined)), [1]),
                markArea: {
                    silent: true,
                    label: {
                        show: false,
                    },
                    itemStyle: {
                        color: greyBlue,
                        opacity: 0.2,
                    },
                    data: fillwayThresholds.map(fillway => ([
                        {
                            name: fillway.title,
                            yAxis: fillway.month01 + NGFDepth,
                        },
                        {
                            yAxis: fillway.month02 + NGFDepth,
                        },
                    ])),
                },
            })
        }),
    ]

    return (
        <Grid container padding={1} spacing={1} size={12}>
            <ChipLabel
                label={getHydroCote(displayCote).label}
                onClick={() => setDisplayCote(prev => prev === HYDRO_MEASURE_COTE.HEIGHT ? HYDRO_MEASURE_COTE.NGF : HYDRO_MEASURE_COTE.HEIGHT)}
            />
            <Grid size={12} sx={{ overflowX: 'auto' }}>
                <Grid sx={{ width: `${minTotalPx + 1}px` }}> {/* +1 here so that the last splitLine can be displayed */}
                    <EChartV6
                        style={{ height: gridHeight + 120, width: '100%' }}
                        option={{
                            title: {
                                text: i18n.diagraphies,
                                show: false,
                            },
                            axisPointer: {
                                link: [{ yAxisIndex: 'all' }],
                                type: 'line',
                                snap: true,
                            },
                            tooltip: {
                                trigger: 'axis',
                                axisPointer: {
                                    axis: 'y',
                                    snap: true,
                                },
                                formatter: params => {
                                    if (!params.length) return ''
                                    const depth = round(params[0].axisValue, 2)
                                    const lines = params
                                        .filter(p => p.value && !isUndefined(p.value[0]) && p.seriesName !== i18n.height && p.seriesName !== i18n.strait)
                                        .map(p => `<b>${p.seriesName}</b> : ${round(p.value[0], 2)}`)
                                    const matchingFillways = fillwayThresholds.filter(f => depth >= f.month01 && depth <= f.month02)
                                    const fillwayLines = matchingFillways.map(f => `<b>${f.title}</b> : ${f.month01}${i18n.metersSymbol} → ${f.month02}${i18n.metersSymbol}`)
                                    const waterLines = hydroLastMeasures?.length
                                        ? [`<b>${i18n.height}</b> : ${hydroLastMeasures[0].value} ${i18n.metersSymbol}`]
                                        : []
                                    return `<b>${i18n.depthWithUnit}</b> : ${depth}<br/>${[...waterLines, ...fillwayLines, ...lines].join('<br/>')}`
                                },
                            },
                            grid,
                            xAxis,
                            yAxis,
                            series,
                        }}
                        withYZoomModal
                        exportDatas={{
                            fileName: i18n.diagraphies,
                            headers: exportHeaders,
                            datas: exportDatas,
                        }}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default DiagraphieChart